import { useState, useEffect } from 'react';
import { CalendarOff, CheckCircle2, XCircle, Clock, Calendar, Search, User, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { useBranchFilter } from '@/components/BranchFilterContext';

export default function AdminLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const { branch } = useBranchFilter();

  useEffect(() => {
    fetchLeaves(tab);
  }, [tab, branch]);

  const fetchLeaves = async (status) => {
    setLoading(true);
    try {
      const res = await adminAPI.getLeaves(status === 'all' ? null : status, 1, 50, branch);
      setLeaves(res.data.records || res.data);
    } catch (err) {
      toast.error('Failed to load leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await adminAPI.updateLeaveStatus(id, status);
      toast.success(`Leave ${status}`);
      fetchLeaves(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this leave?')) return;
    try {
      await adminAPI.cancelLeave(id);
      toast.success('Leave cancelled');
      fetchLeaves(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const filtered = leaves.filter(l =>
    l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.user_phone?.includes(search)
  );

  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  const statusColor = {
    approved: 'bg-[#026536]/10 text-[#026536] border-0',
    rejected: 'bg-red-50 text-red-500 border-0',
    pending: 'bg-[#dea439]/10 text-[#dea439] border-0',
    auto_rejected: 'bg-red-50 text-red-500 border-0',
    cancelled: 'bg-gray-100 text-gray-400 border-0',
  };

  const typeColor = {
    casual: 'bg-blue-50 text-blue-600 border-0',
    sick: 'bg-red-50 text-red-500 border-0',
    personal: 'bg-purple-50 text-purple-600 border-0',
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 data-testid="admin-leaves-title" className="text-2xl font-extrabold text-[#1a2529] tracking-tight">Leave Requests</h1>
          <p className="text-sm text-[#1a2529]/50 mt-0.5">Review and manage employee leave applications</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white h-10" data-testid="leave-tabs">
            <TabsTrigger value="all" className="font-medium data-[state=active]:text-[#026536]">All</TabsTrigger>
            <TabsTrigger value="pending" className="font-medium data-[state=active]:text-[#dea439] gap-1">
              Pending
              {pendingCount > 0 && <span className="ml-1 h-5 min-w-[20px] rounded-full bg-[#dea439] text-white text-xs flex items-center justify-center px-1.5">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="approved" className="font-medium data-[state=active]:text-[#026536]">Approved</TabsTrigger>
            <TabsTrigger value="rejected" className="font-medium data-[state=active]:text-red-500">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30" size={16} />
          <Input
            data-testid="leave-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by employee name or phone..."
            className="pl-10 h-10 bg-white"
          />
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <CalendarOff className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
              <p className="font-medium text-[#1a2529]/50">No leave requests found</p>
            </div>
          ) : (
            filtered.map((lv, idx) => (
              <div
                key={lv.id}
                data-testid={`admin-leave-${idx}`}
                className="bg-white rounded-xl p-4 shadow-sm border border-border/30 animate-slide-up"
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#026536]/10 flex items-center justify-center text-sm font-bold text-[#026536]">
                      {lv.user_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a2529]">{lv.user_name}</p>
                      <p className="text-xs text-[#1a2529]/40">+91 {lv.user_phone}</p>
                    </div>
                  </div>
                  <Badge className={`${statusColor[lv.status]} text-xs font-semibold capitalize`}>
                    {lv.status === 'auto_rejected' ? 'Quota Exceeded' : lv.status}
                  </Badge>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge className={`${typeColor[lv.leave_type] || typeColor.casual} text-xs capitalize`}>{lv.leave_type}</Badge>
                  <div className="flex items-center gap-1 text-xs text-[#1a2529]/60">
                    <Calendar size={12} />
                    {formatDate(lv.start_date)}
                    {lv.start_date !== lv.end_date && ` - ${formatDate(lv.end_date)}`}
                  </div>
                  <span className="text-xs font-semibold text-[#1a2529]/60">
                    {lv.duration_type === 'half_day' ? 'Half Day' : lv.duration_type === 'hours' ? `${lv.hours}h` : `${lv.days} day${lv.days > 1 ? 's' : ''}`}
                  </span>
                </div>

                {lv.reason && <p className="text-xs text-[#1a2529]/40 italic mb-3">{lv.reason}</p>}

                {lv.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-leave-${idx}`}
                      onClick={() => handleStatus(lv.id, 'approved')}
                      size="sm"
                      className="flex-1 h-9 bg-[#026536] hover:bg-[#026536]/90 text-white font-medium active:scale-[0.98]"
                    >
                      <CheckCircle2 size={14} className="mr-1.5" /> Approve
                    </Button>
                    <Button
                      data-testid={`reject-leave-${idx}`}
                      onClick={() => handleStatus(lv.id, 'rejected')}
                      size="sm"
                      variant="outline"
                      className="flex-1 h-9 border-red-200 text-red-500 hover:bg-red-50 font-medium active:scale-[0.98]"
                    >
                      <XCircle size={14} className="mr-1.5" /> Reject
                    </Button>
                  </div>
                )}
                {(lv.status === 'pending' || lv.status === 'approved') && (
                  <Button
                    data-testid={`cancel-leave-${idx}`}
                    onClick={() => handleCancel(lv.id)}
                    size="sm"
                    variant="ghost"
                    className="mt-2 h-8 text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 font-medium"
                  >
                    <Undo2 size={12} className="mr-1" /> Cancel Leave
                  </Button>
                )}
                {lv.status === 'cancelled' && (
                  <p className="text-[10px] text-gray-400 mt-1">Cancelled{lv.cancelled_by === 'admin' ? ' by Admin' : ' by Employee'}{lv.cancelled_at ? ` on ${formatDate(lv.cancelled_at.split('T')[0])}` : ''}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
